import React, { useState, useMemo, useEffect } from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import { toast } from 'react-toastify';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import moment from 'moment';
import TabPanel from '../../components/TabPanel';
import Filter from './components/Filter';
import Calendar from './components/Calendar';
import Progress from './components/Progress';
import Table from './components/Table';
import useTeamDashboardContext from './context/useTeamDashboardContext';
import {
  postExternalTeamCalendar,
  postExternalTeamProgress,
  postExternalTeamList,
} from '../../api/teamDashboard';

export default function TeamDashboard() {
  const defaultFilter = {
    team: [{ label: 'ALL', value: null }],
    rep_id: { label: 'ALL', value: null },
    group: [{ label: 'ALL', value: null }],
    date_from: moment().startOf('month').format('YYYY-MM-DD'),
    date_to: moment().endOf('month').format('YYYY-MM-DD'),
  };

  const [activeTab, setActiveTab] = useState(0);
  const [filterForm, setFilterForm] = useState(defaultFilter);
  const [clearFilter, setClearFilter] = useState(false);
  const [calendar, setCalendar] = useState({});
  const [selectedDate, setSelectedDate] = useState();
  const [progress, setProgress] = useState({ loading: false, data: {} });
  const [tableData, setTableData] = useState({ loading: false, data: [] });

  const handleChangeActiveTab = (event, newValue) => {
    setActiveTab(newValue);
  };

  const responseCalendar = async () => {
    try {
      setCalendar({
        loading: true,
        data: [],
      });
      const request = {
        team: filterForm?.team[0].value !== null ? filterForm?.team?.map((row) => row.value) : [],
        rep_id: filterForm?.rep_id?.value ?? '',
        group:
          filterForm?.group[0].value !== null ? filterForm?.group?.map((row) => row.value) : [],
        from: filterForm?.date_from,
        to: filterForm?.date_to,
      };

      const response = await postExternalTeamCalendar(request);
      const { code, data } = response.data;

      if (code === '200') {
        const obj = {
          loading: false,
          date_month: moment(filterForm?.date_from).format('YYYY-MM-DD'),
          data: data?.map((row) => ({
            date: row.date,
            count: row.point_person_count.toString(),
            label: row.point_person_count > 0 ? row.point_person_count.toString() : '',
          })),
        };
        setCalendar(obj);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const responseProgress = async () => {
    try {
      setProgress({
        loading: true,
        data: [],
      });
      const request = {
        team: filterForm?.team[0].value !== null ? filterForm?.team?.map((row) => row.value) : [],
        rep_id: filterForm?.rep_id?.value ?? '',
        group:
          filterForm?.group[0].value !== null ? filterForm?.group?.map((row) => row.value) : [],
        from: filterForm?.date_from,
        to: filterForm?.date_to,
      };

      const response = await postExternalTeamProgress(request);
      const { code, data } = response.data;

      if (code === '200') {
        const obj = {
          loading: false,
          data,
        };
        setProgress(obj);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const responseTablelist = async (type = null, date = null) => {
    try {
      setTableData({
        loading: true,
        data: [],
      });

      const request = {
        team: filterForm?.team[0].value !== null ? filterForm?.team?.map((row) => row.value) : [],
        rep_id: filterForm?.rep_id?.value ?? '',
        group:
          filterForm?.group[0].value !== null ? filterForm?.group?.map((row) => row.value) : [],
        from: type === 'calendar' ? date : filterForm?.date_from,
        to: type === 'calendar' ? date : filterForm?.date_to,
      };

      const response = await postExternalTeamList(request);
      const { code, data } = response.data;

      if (code === '200') {
        const obj = {
          loading: false,
          data,
        };
        setTableData(obj);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleGenerateReport = () => {
    setClearFilter(false);
    toast.loading('Loading data please kindly wait');
    setSelectedDate();
    responseCalendar();
    responseProgress();
    responseTablelist();
    toast.dismiss();
  };

  const handleClearReport = () => {
    setSelectedDate();
    setFilterForm(defaultFilter);
    setClearFilter(true);
  };

  const handleClickDate = (dateData) => {
    setSelectedDate(moment(dateData?.date).format('MMMM DD YYYY'));
    responseTablelist('calendar', dateData?.date);
  };
  const handleResetTable = () => {
    setSelectedDate();
    responseTablelist();
  };

  useEffect(() => {
    handleGenerateReport();
  }, [activeTab, clearFilter]);
  return (
    <div style={{ position: 'relative' }}>
      <useTeamDashboardContext.Provider
        value={useMemo(
          () => ({
            filterForm,
            calendar,
            progress,
            tableData,
            selectedDate,
            setFilterForm,
            setTableData,
            handleClickDate,
            handleGenerateReport,
            handleClearReport,
            handleResetTable,
          }),
          [filterForm, calendar, progress, tableData],
        )}
      >
        <Box sx={{ backgroundColor: '#fff', p: 1, boxShadow: 3 }}>
          <Filter />
        </Box>
        <Box padding={4}>
          <Grid container spacing={2}>
            <Grid xs={6} sm={6} md={6}>
              <Calendar />
            </Grid>
            <Grid xs={6} sm={6} md={6}>
              <Progress />
            </Grid>
          </Grid>
          <Box>
            <Box>
              <Grid container spacing={2}>
                <Grid xs={12} sm={12} md={12}>
                  <Box sx={{ backgroundColor: '#fff' }} mt={4}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      <Tabs
                        value={activeTab}
                        onChange={handleChangeActiveTab}
                        aria-label='basic tabs example'
                      >
                        <Tab
                          label='Team Dashboard'
                          id='team-dashboard'
                          aria-controls='team-dashboard'
                        />
                      </Tabs>
                    </Box>
                    <TabPanel value={activeTab} index={activeTab}>
                      <Table />
                    </TabPanel>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </useTeamDashboardContext.Provider>
    </div>
  );
}
